import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import coinImg from "../assets/178F722D-F252-429C-9773-CB27053B84EF.png";

/* ---------------- helpers ---------------- */
const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

async function authedFetch(path, { method = "GET", body } = {}) {
  let auth = null;
  try {
    auth = JSON.parse(localStorage.getItem("auth") || "null");
  } catch {
    auth = null;
  }

  const token = auth?.token;
  if (!token) {
    const err = new Error("No auth token (please login again)");
    err.status = 401;
    throw err;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function fmtDate(iso, lng) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(lng || undefined, { dateStyle: "medium", timeStyle: "short" });
}

function amountColor(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "text-white/80";
  if (n > 0) return "text-[#98dea3]";
  if (n < 0) return "text-red-200";
  return "text-white/70";
}

function CoinAmount({ value, size = 18, className = "", imgClassName = "" }) {
  const amount = Number.isFinite(Number(value)) ? Number(value).toFixed(0) : "0";

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={coinImg}
        alt=""
        className={`shrink-0 object-contain ${imgClassName}`}
        style={{ width: size, height: size }}
      />
      <span>{amount}</span>
    </span>
  );
}

export default function Transactions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [wallet, setWallet] = useState({ balance: 0, currency: "POINTS" });
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [w, tx] = await Promise.all([
        authedFetch("/api/me/wallet"),
        authedFetch("/api/me/transactions"),
      ]);

      setWallet({
        balance: Number.isFinite(Number(w?.balance)) ? Number(w.balance) : 0,
        currency: w?.currency || "POINTS",
      });

      setRows(Array.isArray(tx?.items) ? tx.items : []);
    } catch (e) {
      setErr(e?.message || t("tx.errors.loadFailed", { defaultValue: "Failed to load transactions" }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const lng = i18n.resolvedLanguage || i18n.language || "en";

  const isPointsCurrency = useMemo(() => {
    return String(wallet.currency || "").toUpperCase() === "POINTS";
  }, [wallet.currency]);

  const typeLabel = useCallback(
    (type) => {
      const x = String(type || "").toUpperCase();
      if (x === "SIGNUP_BONUS") return t("tx.types.signupBonus", { defaultValue: "Signup bonus" });
      if (x === "TOP_UP") return t("tx.types.topup", { defaultValue: "Top up" });
      if (x === "REQUEST_FEE") return t("tx.types.requestFee", { defaultValue: "Request fee" });
      if (x === "REFUND") return t("tx.types.refund", { defaultValue: "Refund" });
      return t("tx.types.unknown", { defaultValue: "Transaction" });
    },
    [t]
  );

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-6xl px-4 pt-10 pb-24">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-unbounded text-3xl md:text-4xl font-extrabold tracking-tight">
              {t("tx.title", { defaultValue: "Balance history" })}
            </h1>
            <p className="mt-2 text-white/70">
              {t("tx.subtitle", { defaultValue: "All charges and top ups for your balance." })}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="rounded-xl bg-white/10 px-4 py-2 hover:bg-white/15"
            >
              ← {t("common.back", { defaultValue: "Back" })}
            </button>

            <button
              onClick={loadAll}
              className="rounded-xl border border-white/20 px-4 py-2 hover:bg-white/5"
            >
              {t("actions.refresh", { defaultValue: "Refresh" })}
            </button>
          </div>
        </div>

        <section className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
          <div className="text-sm text-white/70">
            {t("tx.currentBalance", { defaultValue: "Current balance" })}
          </div>

          <div className="mt-2">
            {isPointsCurrency ? (
              <CoinAmount
                value={wallet.balance}
                size={28}
                className="text-3xl font-extrabold tracking-tight text-white"
                imgClassName="drop-shadow-sm"
              />
            ) : (
              <div className="text-3xl font-extrabold tracking-tight">
                {Number(wallet.balance || 0).toFixed(0)} {wallet.currency}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/pay?mode=topup")}
              className="rounded-xl bg-[#7269e3] px-4 py-2 font-semibold hover:brightness-110"
            >
              {t("tx.topupBtn", { defaultValue: "Top up" })}
            </button>
          </div>
        </section>

        <section className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-unbounded text-xl font-semibold">
              {t("tx.listTitle", { defaultValue: "Transactions" })}
            </h2>
            <div className="text-sm text-white/60">
              {t("tx.count", { defaultValue: "Count:" })} {rows.length}
            </div>
          </div>

          {loading ? (
            <div className="mt-4 rounded-xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-white/70">
              {t("common.loading", { defaultValue: "Loading…" })}
            </div>
          ) : err ? (
            <div className="mt-4 rounded-xl bg-red-500/10 ring-1 ring-red-400/20 p-6 text-center text-red-100">
              {err}
            </div>
          ) : rows.length === 0 ? (
            <div className="mt-4 rounded-xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-white/70">
              {t("tx.empty", { defaultValue: "No transactions yet." })}
            </div>
          ) : (
            <>
            {/* Mobile: stacked cards — the 4-column table was wider than the
                phone screen and got clipped (last column «Запит» unreachable,
                no horizontal scroll). Cards show every field without scrolling. */}
            <div className="mt-4 space-y-2 sm:hidden">
              {rows.map((r) => {
                const numericAmount = Number(r.amount || 0);
                const showPlus = numericAmount > 0;
                const isPointsRow = String(wallet.currency || "").toUpperCase() === "POINTS";
                return (
                  <div key={r.id} className="rounded-xl bg-[#1f2430]/40 ring-1 ring-white/10 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-white/60">{fmtDate(r.createdAt, lng)}</span>
                      <span className={`font-semibold ${amountColor(r.amount)}`}>
                        {isPointsRow ? (
                          <span className="inline-flex items-center">
                            {showPlus ? <span className="mr-1">+</span> : null}
                            {numericAmount < 0 ? <span className="mr-1">−</span> : null}
                            <CoinAmount value={Math.abs(numericAmount)} size={18} className="font-semibold" imgClassName="drop-shadow-sm" />
                          </span>
                        ) : (
                          <>{showPlus ? "+" : ""}{numericAmount.toFixed(0)} {wallet.currency}</>
                        )}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="rounded-lg bg-white/10 px-2 py-1 text-xs">{typeLabel(r.type)}</span>
                      {r.requestId ? (
                        <button onClick={() => navigate(`/requests/${r.requestId}`)} className="rounded-lg bg-white/10 px-2 py-1 text-xs hover:bg-white/15">
                          {t("tx.openRequest", { defaultValue: "Open" })}
                        </button>
                      ) : (
                        <span className="text-xs text-white/40">{t("common.notSpecified", { defaultValue: "—" })}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 hidden overflow-x-auto rounded-xl ring-1 ring-white/10 sm:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-white/70">
                  <tr>
                    <th className="px-4 py-3">{t("tx.th.date", { defaultValue: "Date" })}</th>
                    <th className="px-4 py-3">{t("tx.th.type", { defaultValue: "Type" })}</th>
                    <th className="px-4 py-3">{t("tx.th.amount", { defaultValue: "Amount" })}</th>
                    <th className="px-4 py-3">{t("tx.th.request", { defaultValue: "Request" })}</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-white/10">
                  {rows.map((r) => {
                    const numericAmount = Number(r.amount || 0);
                    const showPlus = numericAmount > 0;
                    const isPointsRow =
                      String(wallet.currency || "").toUpperCase() === "POINTS";

                    return (
                      <tr key={r.id} className="bg-[#1f2430]/30">
                        <td className="px-4 py-3 text-white/80">{fmtDate(r.createdAt, lng)}</td>

                        <td className="px-4 py-3">
                          <span className="rounded-lg bg-white/10 px-2 py-1">{typeLabel(r.type)}</span>
                        </td>

                        <td className={`px-4 py-3 font-semibold ${amountColor(r.amount)}`}>
                          {isPointsRow ? (
                            <span className="inline-flex items-center">
                              {showPlus ? <span className="mr-1">+</span> : null}
                              {numericAmount < 0 ? <span className="mr-1">−</span> : null}
                              <CoinAmount
                                value={Math.abs(numericAmount)}
                                size={18}
                                className="font-semibold"
                                imgClassName="drop-shadow-sm"
                              />
                            </span>
                          ) : (
                            <>
                              {showPlus ? "+" : ""}
                              {numericAmount.toFixed(0)} {wallet.currency}
                            </>
                          )}
                        </td>

                        <td className="px-4 py-3 text-white/70">
                          {r.requestId ? (
                            <button
                              onClick={() => navigate(`/requests/${r.requestId}`)}
                              className="rounded-lg bg-white/10 px-2 py-1 hover:bg-white/15"
                            >
                              {t("tx.openRequest", { defaultValue: "Open" })}
                            </button>
                          ) : (
                            <span className="text-white/40">
                              {t("common.notSpecified", { defaultValue: "—" })}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}